import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDInput from "@/components/atoms/MDInput";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import PageLayout from "@/components/templates/PageLayout";
import useCreateSku from "@/hooks/requests/sku/use-create-sku";
import { SIDEBAR_MESSAGES } from "@/messages/layout/sidebar";

interface SkuFormPageProps {
  messages: Record<string, string | Record<string, string>>; // SKU form messages type
}

interface SkuFormData {
  code: string;
  productId: number;
  productCode: string;
  price: number;
  stock: number;
  weight: number;
  description: string;
  isActive: boolean;
}

const SkuFormPage: React.FC<SkuFormPageProps> = ({ messages }) => {
  const {
    productId,
    productCode,
    lang = "PT-BR",
  } = useParams<{
    productId: string;
    productCode: string;
    lang: string;
  }>();
  const navigate = useNavigate();
  const { isLoading, create } = useCreateSku();

  const [formData, setFormData] = useState<SkuFormData>({
    code: "",
    productId: productId ? parseInt(productId, 10) : 0,
    productCode: productCode || "",
    price: 0,
    stock: 0,
    weight: 0,
    description: "",
    isActive: true,
  });

  const pageMessages = messages[lang as keyof typeof messages] as Record<string, string>;

  useEffect(() => {
    if (productId && productCode) {
      setFormData((prev) => ({
        ...prev,
        productId: parseInt(productId, 10),
        productCode,
      }));
    }
  }, [productId, productCode]);

  const handleInputChange =
    (field: keyof SkuFormData) => (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value, type, checked } = event.target;
      setFormData((prev) => ({
        ...prev,
        [field]: type === "checkbox" ? checked : type === "number" ? Number(value) : value,
      }));
    };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      const skuData = {
        id: 0,
        code: formData.code,
        title: formData.description, // Map description to title
        productId: formData.productId,
        depth: 0, // Default values
        mainPrice: 0,
        active: formData.isActive,
        rowDate: new Date(), // Use Date object instead of string
        productCode: formData.productCode,
        stock: formData.stock,
        weight: formData.weight,
        promoPrice: 0, // Required fields
        kitComponents: [],
        kitsToDelete: [],
      };

      await create({
        formData: skuData,
      });

      // Navigate back to product details
      navigate(`/products/${productId}`);
    } catch (error) {
      // Error creating SKU
    }
  };

  const handleCancel = () => {
    navigate(`/products/${productId}`);
  };

  return (
    <PageLayout messages={SIDEBAR_MESSAGES}>
      <PageContainer>
        <MDBox p={3}>
          <MDBox mb={3}>
            <MDTypography variant="h4" fontWeight="medium">
              {pageMessages.new_sku || "Novo SKU"}
            </MDTypography>
            <MDTypography variant="body2" color="text">
              {pageMessages.create_sku_for_product || `Criar SKU para produto ${productCode}`}
            </MDTypography>
          </MDBox>

          <MDBox component="form" onSubmit={handleSubmit}>
            <MDBox mb={3}>
              <MDInput
                label={pageMessages.sku_code || "Código do SKU"}
                value={formData.code}
                onChange={handleInputChange("code")}
                required
                fullWidth
              />
            </MDBox>

            <MDBox mb={3}>
              <MDTypography variant="caption" color="text" sx={{ display: "block", mb: 0.5 }}>
                {pageMessages.description || "Descrição"}
              </MDTypography>
              <MDInput
                value={formData.description}
                onChange={handleInputChange("description")}
                multiline
                rows={3}
                fullWidth
              />
            </MDBox>

            <MDBox mb={3}>
              <MDInput
                label={pageMessages.price || "Preço"}
                type="number"
                value={formData.price}
                onChange={handleInputChange("price")}
                inputProps={{ step: "0.01", min: "0" }}
                fullWidth
              />
            </MDBox>

            <MDBox mb={3}>
              <MDInput
                label={pageMessages.stock || "Estoque"}
                type="number"
                value={formData.stock}
                onChange={handleInputChange("stock")}
                inputProps={{ min: "0" }}
                fullWidth
              />
            </MDBox>

            <MDBox mb={3}>
              <MDInput
                label={pageMessages.weight || "Peso (kg)"}
                type="number"
                value={formData.weight}
                onChange={handleInputChange("weight")}
                inputProps={{ step: "0.01", min: "0" }}
                fullWidth
              />
            </MDBox>

            <MDBox mb={3}>
              <label>
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={handleInputChange("isActive")}
                />
                {pageMessages.active || "Ativo"}
              </label>
            </MDBox>

            <MDBox display="flex" justifyContent="flex-end" gap={2}>
              <MDButton type="submit" variant="contained" color="primary" disabled={isLoading}>
                {isLoading ? pageMessages.creating || "Criando..." : pageMessages.create || "Criar"}
              </MDButton>
              <MDButton type="button" variant="outlined" color="secondary" onClick={handleCancel}>
                {pageMessages.cancel || "Cancelar"}
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </PageContainer>
    </PageLayout>
  );
};

export default SkuFormPage;
